import React from 'react';
import { useThemeColors } from '../../hooks/useThemeColors';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const pretty = (value) => {
  if (!value || typeof value !== 'string') return value;
  return value
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

// Deterministic pastel color from a string (for avatars)
const colorFromString = (str = '') => {
  const palette = [
    { bg: '#EEF2FF', fg: '#4F46E5' }, // indigo
    { bg: '#ECFDF5', fg: '#10B981' }, // emerald
    { bg: '#FFFBEB', fg: '#D97706' }, // amber
    { bg: '#FEF2F2', fg: '#EF4444' }, // red
    { bg: '#F5F3FF', fg: '#8B5CF6' }, // violet
    { bg: '#ECFEFF', fg: '#0891B2' }, // cyan
    { bg: '#FDF2F8', fg: '#DB2777' }, // pink
    { bg: '#F0FDF4', fg: '#16A34A' }, // green
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

/* -------------------------------------------------------------------------- */
/* Status / priority configs                                                  */
/* -------------------------------------------------------------------------- */

const trackingStatusConfig = {
  waiting: { label: 'Waiting', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  recently_sent: { label: 'Recently Sent', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  follow_up: { label: 'Follow Up', color: '#0E7490', bg: '#ECFEFF', border: '#A5F3FC' },
  converted: { label: 'Converted', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  lost: { label: 'Lost', color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' },
};

const projectStatusConfig = {
  active: { label: 'Active', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  ready_to_schedule: { label: 'Ready to Schedule', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  on_hold: { label: 'On Hold', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  completed: { label: 'Completed', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  cancelled: { label: 'Cancelled', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  archived: { label: 'Archived', color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' },
};

const priorityConfig = {
  urgent: { label: 'Urgent', dot: '#EF4444' },
  'very high': { label: 'Very High', dot: '#EF4444' },
  high: { label: 'High', dot: '#F59E0B' },
  medium: { label: 'Medium', dot: '#3B82F6' },
  normal: { label: 'Normal', dot: '#3B82F6' },
  low: { label: 'Low', dot: '#9CA3AF' },
};

const getTrackingStatus = (key) => trackingStatusConfig[key?.toLowerCase()] || null;
const getProjectStatus = (key) => projectStatusConfig[key?.toLowerCase()] || null;
const getPriority = (key) => priorityConfig[key?.toLowerCase()] || null;

/* -------------------------------------------------------------------------- */
/* Icons                                                                      */
/* -------------------------------------------------------------------------- */

const Icon = {
  Phone: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z" />
    </svg>
  ),
  Mail: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  ),
  Pin: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Clock: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  Arrow: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
};

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

const IconChip = ({ icon, value, href, label }) => {
  if (!value || value === 'N/A' || value === '—') return null;
  const content = (
    <div className="group/chip flex items-center gap-2 min-w-0">
      <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-gray-50 text-gray-500 group-hover/chip:bg-gray-100 group-hover/chip:text-gray-700 transition-colors shrink-0">
        {icon}
      </span>
      <span className="text-[12.5px] text-gray-700 truncate font-medium" title={typeof value === 'string' ? value : label}>
        {value}
      </span>
    </div>
  );
  if (href) {
    return (
      <a href={href} onClick={(e) => e.stopPropagation()} className="block min-w-0">
        {content}
      </a>
    );
  }
  return content;
};

const StatusPill = ({ config }) => {
  if (!config) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.color,
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: config.color }} />
      {config.label}
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/* Card                                                                       */
/* -------------------------------------------------------------------------- */

const Card = ({ row, onRowClick, onCheckRow, selected, accentColor }) => {
  const jobNumber = row.jobNumber;
  const contactName =
    row['leadContact.name'] ||
    row['siteContact.name'] ||
    row['client.name'] ||
    row.name ||
    '';
  const email = row['leadContact.email'] || row['client.email'] || '';
  const phone = row['leadContact.phone'] || row['client.cellPhone'] || row['leadContact.cellPhone'] || '';
  const city = row['address.city'] || row['siteContact.address.city'] || '';
  const street = row['address.street'] || row['siteContact.address.street'] || '';
  const locationDisplay = [street, city].filter(Boolean).join(', ');

  const tracking = getTrackingStatus(row.trackingStatus);
  const projectStatusCfg = getProjectStatus(row.status || row.projectStatus);
  const priority = getPriority(row.priority);

  const statusCfg = tracking || projectStatusCfg;
  const created = row.createdAt;

  const avatarColor = colorFromString(contactName || jobNumber || 'JRT');

  const handleClick = () => onRowClick && onRowClick(row);
  const handleCheck = (e) => {
    e.stopPropagation();
    onCheckRow && onCheckRow(row.id || row._id);
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative bg-white rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden border ${
        selected
          ? 'shadow-lg ring-2'
          : 'border-gray-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_12px_28px_-12px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 hover:border-gray-300'
      }`}
      style={
        selected
          ? {
              borderColor: 'transparent',
              '--tw-ring-color': accentColor,
              boxShadow: `0 12px 28px -12px ${accentColor}33, 0 0 0 1px ${accentColor}20`,
            }
          : undefined
      }
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-start gap-3">
        {/* Avatar with status dot */}
        <div className="relative shrink-0">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-[15px] font-bold"
            style={{ backgroundColor: avatarColor.bg, color: avatarColor.fg }}
          >
            {getInitials(contactName || jobNumber || '?')}
          </div>
          {statusCfg && (
            <span
              className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white"
              style={{ backgroundColor: statusCfg.color }}
              title={statusCfg.label}
            />
          )}
        </div>

        {/* Title block */}
        <div className="flex-1 min-w-0">
          {jobNumber && (
            <p className="text-[10px] font-mono font-semibold tracking-[0.08em] text-gray-400 uppercase mb-0.5">
              JOB #{jobNumber}
            </p>
          )}
          <h3 className="text-[15px] font-semibold text-gray-900 truncate leading-tight">
            {contactName || `Item ${row.id || row._id || ''}`}
          </h3>
          {city && (
            <p className="text-[11.5px] text-gray-500 mt-0.5 flex items-center gap-1 truncate">
              <span className="text-gray-400">{Icon.Pin}</span>
              <span className="truncate">{city}</span>
            </p>
          )}
        </div>

        {/* Checkbox */}
        <label
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-50 cursor-pointer shrink-0 -mr-1 -mt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={handleCheck}
            className="w-4 h-4 rounded border-gray-300 cursor-pointer"
            style={{ accentColor }}
          />
        </label>
      </div>

      {/* Status pill */}
      {statusCfg && (
        <div className="px-5 pb-3">
          <StatusPill config={statusCfg} />
        </div>
      )}

      {/* Body — quick info */}
      {(phone || email || locationDisplay) && (
        <div className="px-5 pb-4 space-y-2">
          {phone && phone !== 'N/A' && (
            <IconChip icon={Icon.Phone} value={phone} href={`tel:${phone}`} label="Phone" />
          )}
          {email && email !== 'N/A' && (
            <IconChip icon={Icon.Mail} value={email} href={`mailto:${email}`} label="Email" />
          )}
          {locationDisplay && (
            <IconChip icon={Icon.Pin} value={locationDisplay} label="Location" />
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5 text-[11px] text-gray-500 min-w-0">
          {created && (
            <span className="inline-flex items-center gap-1 truncate">
              <span className="text-gray-400">{Icon.Clock}</span>
              {formatRelativeTime(created)}
            </span>
          )}
          {priority && created && <span className="text-gray-300">·</span>}
          {priority && (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: priority.dot }} />
              {priority.label}
            </span>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all"
          style={{ color: accentColor, backgroundColor: `${accentColor}10` }}
        >
          View
          <span className="transition-transform group-hover:translate-x-0.5">
            {Icon.Arrow}
          </span>
        </button>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* CardView                                                                   */
/* -------------------------------------------------------------------------- */

const CardView = ({ data = [], onRowClick, onCheckRow, selectedRows = [] }) => {
  const { buttonColors } = useThemeColors();
  const accentColor = buttonColors?.primary || '#6366F1';

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
          <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-900">No items to display</p>
        <p className="text-xs text-gray-500 mt-1">Try changing filters or create a new entry</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 pb-4">
      {data.map((row) => {
        const id = row.id || row._id;
        return (
          <Card
            key={id}
            row={row}
            onRowClick={onRowClick}
            onCheckRow={onCheckRow}
            selected={selectedRows.includes(id)}
            accentColor={accentColor}
          />
        );
      })}
    </div>
  );
};

export default CardView;
